import { useId, useMemo, type ReactElement } from 'react';
import type { AircraftType, AircraftTypeId, Registration } from '../../../domain/models';
import {
  ACQUISITION_MODES,
  ACQUISITION_MODE_LABELS,
  type AcquisitionMode,
  type EditorSourceKind,
  type FleetCabinTarget,
} from './cabinEditorTypes';
import { formatInt, formatKgHr, formatUnits } from './cabinFormat';

export interface AircraftSelectorProps {
  readonly aircraftTypes: readonly AircraftType[];
  readonly fleet: readonly FleetCabinTarget[];
  readonly sourceKind: EditorSourceKind;
  readonly typeId: AircraftTypeId;
  readonly registration: Registration | null;
  readonly acquisitionMode: AcquisitionMode;
  /** The type whose cabin is being edited; drives the fact strip. */
  readonly activeType: AircraftType;
  readonly onSourceKindChange: (kind: EditorSourceKind) => void;
  readonly onTypeChange: (typeId: AircraftTypeId) => void;
  readonly onRegistrationChange: (registration: Registration) => void;
  readonly onAcquisitionModeChange: (mode: AcquisitionMode) => void;
}

function compareTypes(a: AircraftType, b: AircraftType): number {
  return a.familyId.localeCompare(b.familyId) || a.id.localeCompare(b.id);
}

/** Chooses what to edit: a pending acquisition of any type, or a fleet aircraft. */
export function AircraftSelector({
  aircraftTypes,
  fleet,
  sourceKind,
  typeId,
  registration,
  acquisitionMode,
  activeType,
  onSourceKindChange,
  onTypeChange,
  onRegistrationChange,
  onAcquisitionModeChange,
}: AircraftSelectorProps): ReactElement {
  const uid = useId();

  const typesByFamily = useMemo(() => {
    const families = new Map<string, AircraftType[]>();
    for (const type of [...aircraftTypes].sort(compareTypes)) {
      const members = families.get(type.familyId);
      if (members === undefined) {
        families.set(type.familyId, [type]);
      } else {
        members.push(type);
      }
    }
    return [...families.entries()];
  }, [aircraftTypes]);

  const sortedFleet = useMemo(
    () => [...fleet].sort((a, b) => a.registration.localeCompare(b.registration)),
    [fleet]
  );

  const fleetAvailable = sortedFleet.length > 0;

  return (
    <section className="ce-card ce-selector" aria-labelledby={`${uid}-title`}>
      <h3 id={`${uid}-title`} className="ce-card__title">
        Aircraft
      </h3>

      <div className="ce-segmented" role="radiogroup" aria-label="Cabin source">
        <label className="ce-segmented__option">
          <input
            type="radio"
            name={`${uid}-source`}
            value="ACQUISITION"
            checked={sourceKind === 'ACQUISITION'}
            onChange={(): void => onSourceKindChange('ACQUISITION')}
          />
          <span>Pending acquisition</span>
        </label>
        <label className="ce-segmented__option" aria-disabled={!fleetAvailable}>
          <input
            type="radio"
            name={`${uid}-source`}
            value="FLEET"
            checked={sourceKind === 'FLEET'}
            disabled={!fleetAvailable}
            onChange={(): void => onSourceKindChange('FLEET')}
          />
          <span>Fleet aircraft{fleetAvailable ? ` (${formatInt(sortedFleet.length)})` : ''}</span>
        </label>
      </div>

      {sourceKind === 'ACQUISITION' ? (
        <div className="ce-selector__fields">
          <div className="ce-field">
            <label className="ce-field__label" htmlFor={`${uid}-type`}>
              Aircraft type
            </label>
            <select
              id={`${uid}-type`}
              className="ce-select"
              value={typeId}
              onChange={(event): void => onTypeChange(event.target.value)}
            >
              {typesByFamily.map(([familyId, members]) => (
                <optgroup key={familyId} label={familyId}>
                  {members.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.id}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="ce-field">
            <label className="ce-field__label" htmlFor={`${uid}-mode`}>
              Acquisition
            </label>
            <select
              id={`${uid}-mode`}
              className="ce-select"
              value={acquisitionMode}
              onChange={(event): void => {
                const next = ACQUISITION_MODES.find((mode) => mode === event.target.value);
                if (next !== undefined) {
                  onAcquisitionModeChange(next);
                }
              }}
            >
              {ACQUISITION_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {ACQUISITION_MODE_LABELS[mode]}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="ce-selector__fields">
          <div className="ce-field">
            <label className="ce-field__label" htmlFor={`${uid}-reg`}>
              Registration
            </label>
            <select
              id={`${uid}-reg`}
              className="ce-select"
              value={registration ?? ''}
              onChange={(event): void => onRegistrationChange(event.target.value)}
            >
              {sortedFleet.map((aircraft) => (
                <option key={aircraft.registration} value={aircraft.registration}>
                  {aircraft.registration} · {aircraft.typeId}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <dl className="ce-facts">
        <div className="ce-facts__item">
          <dt>Type</dt>
          <dd>{activeType.id}</dd>
        </div>
        <div className="ce-facts__item">
          <dt>Floor budget</dt>
          <dd>{formatUnits(activeType.cabinFloorUnits)} units</dd>
        </div>
        <div className="ce-facts__item">
          <dt>Certified seats</dt>
          <dd>{formatInt(activeType.maxCertifiedSeats)}</dd>
        </div>
        <div className="ce-facts__item">
          <dt>Base burn</dt>
          <dd>{formatKgHr(activeType.baseFuelBurnKgHr)}</dd>
        </div>
        <div className="ce-facts__item">
          <dt>Turnaround</dt>
          <dd>{formatInt(activeType.minTurnaroundMinutes)} min</dd>
        </div>
      </dl>
    </section>
  );
}
